/**
 * ETL designer — pure graph logic (ADR-0038, P4).
 *
 * Framework-agnostic: effective (static + dynamic) ports, connection +
 * pipeline validation (cycle / source / sink / required fields), and the
 * canvas↔definition converters. Kept free of ReactFlow imports so it's unit
 * testable with plain object literals (ReactFlow's `Node`/`Edge` are
 * structurally compatible with the minimal shapes below).
 *
 * Port of the Fluxo `validation.ts` / `dynamic-ports.ts` / `pipeline-converter.ts`.
 */
import type {
  BlockCategory,
  BlockConfigField,
  BlockDescriptor,
  BlockDynamicPorts,
  BlockPort,
  EtlEdgeDef,
  EtlNodeDef,
  EtlPipelineDefinitionDoc,
  NodeMetricSnapshot,
} from "@/lib/api-etl";

/** A config field is shown unless its `visibleWhen` condition fails. The
 * `value` may be a single value (exact match) OR an array (the dependency must
 * be one of them — n8n-style multi-value `displayOptions.show`); when no value
 * is given the dependency just has to be truthy. */
export function isFieldVisible(field: BlockConfigField, config: Record<string, unknown>): boolean {
  if (!field.visibleWhen) return true;
  const dep = config[field.visibleWhen.dependsOn];
  const want = field.visibleWhen.value;
  if (want === undefined) {
    return dep !== undefined && dep !== null && dep !== "";
  }
  if (Array.isArray(want)) {
    return want.includes(dep);
  }
  return dep === want;
}

/** ReactFlow node `data` payload for an ETL block. */
export interface EtlNodeData {
  label: string;
  blockType: string;
  descriptor?: BlockDescriptor;
  config: Record<string, unknown>;
  inputs: BlockPort[];
  outputs: BlockPort[];
  /** Live run status (overlaid from SSE metrics): idle/running/done/failed. */
  runStatus?: "idle" | "running" | "done" | "failed";
  /** Latest per-node counters from the run (SSE etl_run_metrics/finished). */
  metrics?: NodeMetricSnapshot;
  [k: string]: unknown;
}

/** Minimal node/edge shapes — ReactFlow's `Node`/`Edge` satisfy these. */
export interface GraphNode {
  id: string;
  data: EtlNodeData;
}
export interface GraphEdge {
  id?: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

const DEFAULT_PORT_COLOR = "#94a3b8";

// ---------------------------------------------------------------------------
// Dynamic + effective ports
// ---------------------------------------------------------------------------

function applyTemplate(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? "");
}

/** Generate dynamic ports from a config field — array-based (one per item's
 * channel, e.g. router `routes`) or count-based (N ports from a numeric field). */
export function generateDynamicPorts(
  spec: BlockDynamicPorts | null | undefined,
  config: Record<string, unknown>,
): BlockPort[] {
  if (!spec) return [];
  const raw = config[spec.sourceField];

  // Array-based: one port per array item, channel read from `channelProperty`.
  if (spec.channelProperty) {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const ports: BlockPort[] = [];
    raw.forEach((item, i) => {
      const channel =
        item && typeof item === "object"
          ? String((item as Record<string, unknown>)[spec.channelProperty!] ?? "")
          : "";
      if (!channel || seen.has(channel)) return;
      seen.add(channel);
      ports.push({
        id: applyTemplate(spec.namePattern ?? "{channel}", { channel, i: String(i + 1) }),
        label: applyTemplate(spec.labelPattern ?? "{channel}", { channel, i: String(i + 1) }),
        color: DEFAULT_PORT_COLOR,
        isAlwaysVisible: false,
      });
    });
    return ports;
  }

  // Count-based: N ports from a numeric field.
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 1) return [];
  const ports: BlockPort[] = [];
  for (let i = 1; i <= Math.min(Math.trunc(n), 100); i++) {
    ports.push({
      id: applyTemplate(spec.namePattern ?? "input{i}", { i: String(i) }),
      label: applyTemplate(spec.labelPattern ?? "Input {i}", { i: String(i) }),
      color: DEFAULT_PORT_COLOR,
      isAlwaysVisible: false,
    });
  }
  return ports;
}

function mergePorts(a: BlockPort[], b: BlockPort[]): BlockPort[] {
  const seen = new Set(a.map((p) => p.id));
  return [...a, ...b.filter((p) => !seen.has(p.id))];
}

export function getEffectiveInputPorts(
  descriptor: BlockDescriptor | undefined,
  config: Record<string, unknown>,
): BlockPort[] {
  if (!descriptor) return [];
  return mergePorts(descriptor.inputs ?? [], generateDynamicPorts(descriptor.dynamicInputs, config));
}

export function getEffectiveOutputPorts(
  descriptor: BlockDescriptor | undefined,
  config: Record<string, unknown>,
): BlockPort[] {
  if (!descriptor) return [];
  return mergePorts(descriptor.outputs ?? [], generateDynamicPorts(descriptor.dynamicOutputs, config));
}

// ---------------------------------------------------------------------------
// Connection validation (+ cycle detection)
// ---------------------------------------------------------------------------

export interface ConnectionCheck {
  valid: boolean;
  error?: string;
}

export function wouldCreateCycle(source: string, target: string, edges: GraphEdge[]): boolean {
  const adj = new Map<string, string[]>();
  for (const e of edges) adj.set(e.source, [...(adj.get(e.source) ?? []), e.target]);
  adj.set(source, [...(adj.get(source) ?? []), target]);

  const visited = new Set<string>();
  const stack = new Set<string>();
  const dfs = (node: string): boolean => {
    visited.add(node);
    stack.add(node);
    for (const next of adj.get(node) ?? []) {
      if (!visited.has(next)) {
        if (dfs(next)) return true;
      } else if (stack.has(next)) {
        return true;
      }
    }
    stack.delete(node);
    return false;
  };
  for (const n of adj.keys()) if (!visited.has(n) && dfs(n)) return true;
  return false;
}

export function validateConnection(
  source: string,
  target: string,
  edges: GraphEdge[],
  nodes: GraphNode[],
  sourceHandle?: string | null,
  targetHandle?: string | null,
): ConnectionCheck {
  if (source === target) return { valid: false, error: "Un blocco non può collegarsi a se stesso" };
  const src = nodes.find((n) => n.id === source);
  const tgt = nodes.find((n) => n.id === target);
  if (!src || !tgt) return { valid: false, error: "Blocco sorgente o destinazione non trovato" };

  if (sourceHandle && !src.data.outputs.some((p) => p.id === sourceHandle)) {
    return { valid: false, error: "Porta di output non valida" };
  }
  if (targetHandle && !tgt.data.inputs.some((p) => p.id === targetHandle)) {
    return { valid: false, error: "Porta di input non valida" };
  }
  // One edge per input port (a consumer is a single channel — fan-in uses
  // the same port from multiple sources, but never two edges on one (target,
  // port) pair from the SAME source/port pair). Block exact duplicates.
  if (edges.some((e) => e.target === target && e.targetHandle === targetHandle && e.source === source && e.sourceHandle === sourceHandle)) {
    return { valid: false, error: "Connessione già presente" };
  }
  if (wouldCreateCycle(source, target, edges)) {
    return { valid: false, error: "La connessione creerebbe un ciclo" };
  }
  return { valid: true };
}

// ---------------------------------------------------------------------------
// Pipeline validation
// ---------------------------------------------------------------------------

export interface PipelineCheck {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

function category(n: GraphNode): BlockCategory | undefined {
  return n.data.descriptor?.ui.category;
}

export function validatePipeline(nodes: GraphNode[], edges: GraphEdge[]): PipelineCheck {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (nodes.length === 0) {
    return { valid: false, errors: ["La pipeline è vuota"], warnings };
  }
  if (!nodes.some((n) => category(n) === "source")) {
    errors.push("Serve almeno un blocco sorgente");
  }
  if (!nodes.some((n) => category(n) === "sink")) {
    errors.push("Serve almeno un blocco sink");
  }

  for (const n of nodes) {
    for (const f of n.data.descriptor?.configFields ?? []) {
      if (!f.required) continue;
      const v = n.data.config[f.key];
      if (v === undefined || v === null || v === "") {
        errors.push(`"${n.data.label}": campo obbligatorio "${f.label}" mancante`);
      }
    }
  }

  for (const e of edges) {
    const src = nodes.find((n) => n.id === e.source);
    const tgt = nodes.find((n) => n.id === e.target);
    if (!src || !tgt) {
      errors.push("Un arco punta a un blocco inesistente");
      continue;
    }
    if (e.sourceHandle && !src.data.outputs.some((p) => p.id === e.sourceHandle)) {
      errors.push(`"${src.data.label}" non ha la porta di output "${e.sourceHandle}"`);
    }
    if (e.targetHandle && !tgt.data.inputs.some((p) => p.id === e.targetHandle)) {
      errors.push(`"${tgt.data.label}" non ha la porta di input "${e.targetHandle}"`);
    }
  }

  // Warn on nodes with no incoming edge that aren't sources (orphans).
  const hasIncoming = new Set(edges.map((e) => e.target));
  for (const n of nodes) {
    if (category(n) !== "source" && !hasIncoming.has(n.id)) {
      warnings.push(`"${n.data.label}" non ha connessioni in ingresso`);
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

// ---------------------------------------------------------------------------
// Canvas ↔ definition converters
// ---------------------------------------------------------------------------

export function toDefinition(
  meta: { name: string; description?: string },
  nodes: GraphNode[],
  edges: GraphEdge[],
  positions: Record<string, { x: number; y: number }> = {},
  settings: Record<string, unknown> = {},
): EtlPipelineDefinitionDoc {
  return {
    name: meta.name,
    description: meta.description,
    // Preserve runtime knobs (maxDegreeOfParallelism / boundedCapacity / sampleRows
    // / …) across a save — they live in the version definition, NOT a separate row.
    ...(Object.keys(settings).length ? { settings } : {}),
    nodes: nodes.map(
      (n): EtlNodeDef => ({
        id: n.id,
        type: n.data.blockType,
        label: n.data.label,
        config: n.data.config ?? {},
        position: positions[n.id],
      }),
    ),
    edges: edges.map(
      (e): EtlEdgeDef => ({
        id: e.id,
        from: e.source,
        to: e.target,
        channel: e.sourceHandle ?? undefined,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
      }),
    ),
  };
}

/** Build the node `data` for one definition node, resolving ports from its
 * descriptor + config (defaults applied for unset fields). */
export function nodeDataFromDef(
  def: EtlNodeDef,
  descriptor: BlockDescriptor | undefined,
): EtlNodeData {
  const config: Record<string, unknown> = { ...(def.config ?? {}) };
  for (const f of descriptor?.configFields ?? []) {
    if (config[f.key] === undefined && f.defaultValue !== undefined) {
      config[f.key] = f.defaultValue;
    }
  }
  return {
    label: def.label || descriptor?.ui.label || def.type,
    blockType: def.type,
    descriptor,
    config,
    inputs: getEffectiveInputPorts(descriptor, config),
    outputs: getEffectiveOutputPorts(descriptor, config),
  };
}

export interface FlowGraph {
  nodes: { id: string; type: "etlBlock"; position: { x: number; y: number }; data: EtlNodeData }[];
  edges: {
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
  }[];
}

export function fromDefinition(
  def: EtlPipelineDefinitionDoc,
  descriptors: Map<string, BlockDescriptor>,
): FlowGraph {
  const nodes = (def.nodes ?? []).map((nd, i) => ({
    id: nd.id,
    type: "etlBlock" as const,
    // A block card is ~360px wide — the old 220px step left position-less
    // blocks nearly touching (the "troppo vicini" fallback bug).
    position: nd.position ?? { x: 80 + i * 440, y: 120 },
    data: nodeDataFromDef(nd, descriptors.get(nd.type)),
  }));
  const edges = (def.edges ?? []).map((ed, i) => ({
    id: ed.id || `${ed.from}-${ed.to}-${i}`,
    source: ed.from,
    target: ed.to,
    sourceHandle: ed.sourceHandle || ed.channel || "default",
    targetHandle: ed.targetHandle || "input",
  }));
  return { nodes, edges };
}
